import { ExtendedFormatSettings } from '@mbrtargeting/metatag-shared-types';
import { AdEngineConfigureParams, AdLibraryInterface, AdvertJson, ExternalBuildAdOptions } from '@mbrtargeting/metatag-shared-types/ad-engine';
import { generateRandomNumberString, isNonEmptyString, isString, once } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { loadDependency } from '../../../utils/load-dependency.js';
import { markSdg } from '../../../utils/performance.js';
import { Matcher, waitForPostMessage } from '../../../utils/wait-for-postmessage.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { MetaTagContentObjectWatcher } from '../content-object-watcher/content-object-watcher.js';
import { FormatController } from '../format-controller/format-controller.js';

const log = logger.logGroup({ prefix: 'SYSTEM' });

declare const window: Window & {
    SDG?: MetaTag;
    sdgAdLibrary?: AdLibraryInterface;
};

const START_RENDER_MATCHER: Matcher<{ slotName: string }> = {
    regex: /^SDG_START_RENDER#([a-zA-Z0-9_-]+)/,
    extract: (match) => ({
        slotName: match[1],
    }),
};

const AD_DELIVERED: Matcher<{ slotName: string, width: number, height: number }> = {
    regex: /^SDG_AD_DELIVERED#([a-zA-Z0-9_-]+)#size([0-9]+)x([0-9]+)/,
    extract: (match) => ({
        slotName: match[1],
        width: parseInt(match[2]),
        height: parseInt(match[3])
    })
};

@injectionTarget()
export class LoadAdLibrary {

    @inject() private configResolver!: ConfigResolver;
    @inject() private formatController!: FormatController;
    @inject() private adslotRegistry!: AdslotRegistry;
    @inject() private metaTag!: MetaTag;
    @inject() private contentObjectWatcher!: MetaTagContentObjectWatcher;

    constructor() {
        window.SDG!.buildAd = this.buildAd.bind(this);
        waitForPostMessage(START_RENDER_MATCHER, ({ slotName }) => {
            this.buildAd({
                name: 'CustomRenderer_' + slotName + '_' + generateRandomNumberString(8),
                renderingSlot: slotName,
                format: 'none'
            }, {
                onComplete: () => {
                    log.debug('Finished ad from external creative source for slot: %s', [slotName]);
                }
            });
        }, false);

        waitForPostMessage(AD_DELIVERED, ({ slotName, width, height }) => {
            window.SDG?.Advertising?.getSlotByName(slotName)?.reportAdDelivery(width, height, false);
        }, false);
    }

    #getAdLibrary = once((configParams: AdEngineConfigureParams) => new Promise<AdLibraryInterface>((resolve, reject) => {
        if (window.sdgAdLibrary?.buildAd) {
            resolve(window.sdgAdLibrary);
            return;
        }
        markSdg(`ad-library-requested_mt`);
        const { v2AdEngineFile } = this.configResolver.getCommonSettings();

        loadDependency(
            v2AdEngineFile,
            () => {
                log.debug('AdEngine successfully loaded: %s', [v2AdEngineFile]);
                window.sdgAdLibrary!.configure(configParams);
                resolve(window.sdgAdLibrary!);
            },
            (event) => {
                log.critical('AdEngine could not be loaded: %s', [v2AdEngineFile]);
                reject(event);
            },
        );
    }));

    public async buildAd(json: AdvertJson, options: ExternalBuildAdOptions = {}) {
        const advertising = this.metaTag.Advertising!;
        const formatName = isString(json.format) ? json.format : (json.adFormat || '');
        const formatSettings: Partial<ExtendedFormatSettings> = this.formatController.getFormat(formatName)?.settings || {};
        const slotName = json.renderingSlot;
        const publicAdSlot = advertising.getSlotByName(slotName);
        const internalSlot = this.adslotRegistry.getAdslot(slotName) as Adslot | null;
        const { onComplete, window } = options;

        if (internalSlot?.options.makeSticky) formatSettings.slotSticky = true;

        //used in doubleverify measurement to identify format correctly
        setIdsForAdSlot(slotName, {
            source: 'doubleverify',
            values: {
                formatCode: isNonEmptyString(formatSettings?.formatCode) ? formatSettings.formatCode : 'formatCodeNotSet', //default will help in identifying missing format settings
            }
        });

        const renderInstructions = async () => {
            log.debug('%s: Rendering source: MetaTag-Ad-Engine. Using JSON %o', [slotName.toUpperCase(), json]);

            //load and pass all important features / modules to ad-engine
            const library = await this.#getAdLibrary({
                adhesionUnitWatcher: advertising.getAdhesionUnitWatcher(),
                contentObjectWatcher: this.contentObjectWatcher,
                websiteFeatures: advertising.getWebsiteFeatures(),
                advertising,
            });

            //start rendering process in ad-engine
            await library.buildAd({
                onComplete,
                formatSettings,
                adSlot: publicAdSlot!,
                json,
                deliveringWindow: window
            });
            log.debug('%s: finished rendering ad via AdLibrary.', [slotName.toUpperCase()]);
        };

        if (!publicAdSlot || !internalSlot) return log.error('AD_LIBRARY: Could not determine adSlot to render ad in. Impression will not continue. JSON was: %o', [json]);

        internalSlot.renderCreative(renderInstructions()); //don't await, will mess with stateTransitions
    }
}
